import Image, { ImageProps, ImageLoaderProps } from 'next/image';
import { BREAKPOINTS } from '../../utils/breakpoints';
import {
  generateSizesAttribute,
  RequiredResponsiveSizes,
} from './generateSizesAttribute';
import { getMaxSizeFromUrl, getResponsiveUrl } from './getResponsiveUrl';

export type CmsImageProps = Omit<
  ImageProps,
  'loader' | 'sizes' | 'unoptimized'
> &
  (
    | {
        unoptimized?: false;
        sizes: RequiredResponsiveSizes;
      }
    | {
        unoptimized: true;
        sizes?: RequiredResponsiveSizes;
      }
  );

/**
 * CmsImage — Optimized image component.
 *
 * Sizing Guide (Props vs CSS):
 *
 * 1. `width` / `height` (Props):
 *    - Define Aspect Ratio and reserve layout space before loading (prevent CLS).
 *    - It is NOT necessary to pass real dimensions, only the correct aspect ratio matters.
 *    - Do not affect final visual size if CSS (className) is provided.
 *
 * 2. `className` (CSS):
 *    - Sets the actual displayed size of the element on the page.
 *    - Example: `w-full h-auto` forces the image to take full container width.
 *
 * 3. `sizes` (Props):
 *    - Key parameter for traffic optimization!
 *    - Tells the browser the width of the image *slot* at different breakpoints.
 *    - Browser uses this to download the *minimum necessary* file (e.g. small.jpg for mobile, large.jpg for desktop).
 *    - Does NOT change visual size (that's CSS work), only selects the source file.
 *
 * 4. `unoptimized` (Props):
 *    - If true, disables the custom loader and srcset generation.
 *    - Useful for GIFs, SVGs, or external images where resizing is not possible/desired.
 */
export const CmsImage = ({
  sizes,
  unoptimized = false,
  ...props
}: CmsImageProps) => {
  // 1) When `getMaxSizeFromUrl(src)` is undefined, `getResponsiveUrl(src, width)` will return `src` as-is,
  // meaning the loader ignores `width` (same URL for multiple widths). Next.js detects that and warns:  "loader property does not implement width".
  // 2) Use custom loader only for CMS URLs that support our "size-prefix" variants.
  // For static imports (non-string src) and non-prefixed external URLs, we therefore avoid the loader.
  // If we don't use the custom loader, we also force `unoptimized` to avoid Next.js image optimization.
  const shouldUseCustomLoader =
    !unoptimized &&
    typeof props.src === 'string' &&
    getMaxSizeFromUrl(props.src) !== undefined;

  const loader = ({ src, width }: ImageLoaderProps) => {
    const res = getResponsiveUrl(src, width);
    // console.log('[CmsImage] Requested:', width, src, 'Res:', res);
    return res;
  };

  const sizesAttr = sizes
    ? generateSizesAttribute(sizes, BREAKPOINTS)
    : undefined;

  return (
    <Image
      {...props}
      loader={shouldUseCustomLoader ? loader : undefined}
      unoptimized={unoptimized || !shouldUseCustomLoader}
      sizes={sizesAttr}
    />
  );
};
